import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["orderSelect", "netAmount", "orderAmounts", "totalDisplay"];
  static values = {
    patientId: Number
  }

  connect() {
    // El input siempre trabaja en pesos, el backend se encarga de convertir a centavos
  }

  formatInputValue(event) {
    const input = event.target;
    const value = parseFloat(input.value) || 0;
    // Formatear a 2 decimales para mostrar
    input.value = value.toFixed(2);
  }

  openModal() {
    // Abrir el modal cuando Turbo carga el contenido (Bootstrap 4 con jQuery)
    const modal = document.getElementById('createInvoiceModal');
    if (modal && typeof $ !== 'undefined' && $.fn.modal) {
      // Remover aria-hidden antes de mostrar el modal para evitar warnings de accesibilidad
      modal.removeAttribute('aria-hidden');
      $(modal).modal('show');
    }
  }

  calculateNetAmount() {
    // Usar requestAnimationFrame para asegurar que Slim Select haya actualizado el DOM
    requestAnimationFrame(() => {
      const orderSelect = this.orderSelectTarget;
      if (!orderSelect) return;

      const selectedValues = Array.from(orderSelect.selectedOptions || []);
      const orderAmountsList = document.getElementById("order-amounts-list");
      const orderAmountsDiv = this.orderAmountsTarget;

      if (selectedValues.length > 0) {
        orderAmountsDiv.style.display = "block";
        orderAmountsList.innerHTML = "";

        selectedValues.forEach((option) => {
          const orderId = option.value;
          if (!orderId) return;

          const orderText = option.text; // "Fri 28-Nov-25 | $225 (Efectivo) | Disponible: $225"
          const orderPriceCents = parseInt(option.getAttribute('data-order-price')) || 0;
          const remainingCents = parseInt(option.getAttribute('data-remaining-cents')) || 0;
          const orderPrice = orderPriceCents / 100;
          const remainingPesos = remainingCents / 100;

          // Extraer solo monto y disponible del formato completo
          // Formato: "Fri 28-Nov-25 | $225.00 (Efectivo) | Disponible: $225.00"
          // Queremos mostrar en el label: Lun 15-Dec-25 | Total: $234.00 | Disponible: $234.00
          const textParts = orderText.split(' | ');
          const datePart = textParts.find(part => part.includes('-')) || '';
          // Buscar la parte con el monto (la que tiene $)
          const pricePart = textParts.find(part => part.includes('$') && !part.includes('Disponible')) || `$${orderPrice.toFixed(2)}`;
          const cleanPrice = `Total: ${pricePart.split(' ')[0]}`;
          const availablePart = textParts.find(part => part.includes('Disponible:')) || `Disponible: $${remainingPesos.toFixed(2)}`;
          const displayText = `${datePart} | ${cleanPrice} | ${availablePart}`;

          // Crear campo para monto parcial (trabajando en pesos, se convierte a centavos al enviar)
          const amountDiv = document.createElement("div");
          amountDiv.className = "form-group";
          const inputValue = remainingPesos > 0 ? remainingPesos.toFixed(2) : '0.00';
          amountDiv.innerHTML = `
            <label>${displayText}</label>
            <input type="number"
                   name="invoice[order_amounts][${orderId}]"
                   class="form-control order-amount-input"
                   min="0"
                   max="${remainingPesos.toFixed(2)}"
                   step="0.01"
                   value="${inputValue}"
                   data-order-id="${orderId}"
                   data-order-price="${orderPriceCents}"
                   data-remaining-cents="${remainingCents}"
                   data-action="input->invoice-form#updateTotalAmount input->invoice-form#updateDescriptionFromAmounts blur->invoice-form#formatInputValue">
          `;
          orderAmountsList.appendChild(amountDiv);
        });
      } else {
        orderAmountsDiv.style.display = "none";
        orderAmountsList.innerHTML = "";
      }

      // Actualizar el total después de actualizar los campos
      this.updateTotalAmount();
    });
  }

  updateTotalAmount() {
    const amountInputs = document.querySelectorAll(".order-amount-input");
    let subtotal = 0;

    amountInputs.forEach((input) => {
      const value = parseFloat(input.value) || 0;
      subtotal += value; // Ya está en pesos
    });

    if (this.hasNetAmountTarget) {
      this.netAmountTarget.innerHTML = subtotal.toFixed(2);
    }
  }

  updateDescription() {
    // Usar requestAnimationFrame para asegurar que Slim Select haya actualizado el DOM
    requestAnimationFrame(() => {
      const orderSelect = this.orderSelectTarget;

      if (!orderSelect) {
        console.error('orderSelectTarget no encontrado');
        return;
      }

      // Obtener los valores seleccionados de Slim Select directamente
      let selectedOrderIds = [];

      // Intentar obtener de la instancia de Slim Select
      if (orderSelect.slimSelect) {
        const selectedValues = orderSelect.slimSelect.getSelected();

        selectedOrderIds = selectedValues.filter(id => {
          const keep = id && id !== '';
          return keep;
        });
      } else {
        // Fallback: obtener del select original
        const selectedOptions = Array.from(orderSelect.selectedOptions || []);
        selectedOrderIds = selectedOptions
          .map(opt => opt.value)
          .filter(id => id && id !== '');
      }

      // Si no hay órdenes seleccionadas, limpiar la descripción
      if (selectedOrderIds.length === 0) {
        const descriptionField = document.querySelector('textarea[name="invoice[invoice_description]"]');
        if (descriptionField) {
          descriptionField.value = '';
          descriptionField.dispatchEvent(new Event('input', { bubbles: true }));
        }
        return;
      }

      // Construir descripción para todas las órdenes seleccionadas
      this.buildOrderDescription(selectedOrderIds);
    });
  }

  buildOrderDescription(orderIds) {
    const patientId = this.patientIdValue;

    if (!patientId) {
      console.error('Patient ID not available');
      return;
    }

    // Construir URL del endpoint
    const url = `/patients/${patientId}/invoices_and_payments/order_descriptions?order_ids[]=${orderIds.join('&order_ids[]=')}`;

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      credentials: 'same-origin'
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        const descriptionParts = [];

        // Obtener los montos actuales de los inputs si existen
        const amountInputs = document.querySelectorAll('.order-amount-input');
        const orderAmounts = {};

        amountInputs.forEach(input => {
          const orderId = input.getAttribute('data-order-id');
          const amount = parseFloat(input.value) || 0;
          const totalPriceCents = parseInt(input.getAttribute('data-order-price')) || 0;
          const totalPrice = totalPriceCents / 100;

          if (orderId && amount > 0) {
            orderAmounts[orderId] = {
              amount: amount,
              totalPrice: totalPrice
            };
          }
        });

        // Iterar sobre los orderIds en el orden que fueron seleccionados
        orderIds.forEach((orderId, index) => {
          const orderData = data[orderId.toString()];
          const amountData = orderAmounts[orderId];

          if (orderData) {
            // El backend devuelve la descripción completa con título
            // Extraer título y descripción de bandejas
            const descriptionLines = (orderData.description || '').split('\n');
            let title = '';
            let bandejaDescription = '';

            if (descriptionLines.length > 1 && descriptionLines[0].includes('#Orden')) {
              // Ya tiene título en la primera línea
              title = descriptionLines[0];
              bandejaDescription = descriptionLines.slice(1).join('\n').trim();
            } else {
              // Solo tiene descripción de bandejas (fallback)
              bandejaDescription = orderData.description || '';
              // Construir título básico
              title = `#Orden ${orderData.date} - Total: $${orderData.amount}`;
            }

            // Si hay un monto personalizado diferente, actualizar SOLO el título
            if (amountData && amountData.amount > 0 && amountData.amount !== parseFloat(orderData.remaining_amount)) {
              if (amountData.amount < amountData.totalPrice) {
                // Monto personalizado menor al total, mostrar porcentaje
                const percentage = Math.round((amountData.amount / amountData.totalPrice) * 100);
                const formattedAmount = amountData.amount % 1 === 0 ? amountData.amount.toString() : amountData.amount.toFixed(2);
                title = `#Orden ${orderData.date} - Total: $${formattedAmount} (${percentage}%)`;
              } else {
                // Monto igual al total, sin porcentaje
                title = `#Orden ${orderData.date} - Total: $${orderData.amount}`;
              }
            }

            // Construir descripción completa: título + descripción de bandejas (sin salto extra)
            if (title && bandejaDescription) {
              descriptionParts.push(title);
              descriptionParts.push(bandejaDescription);
            } else if (title) {
              descriptionParts.push(title);
            } else if (bandejaDescription) {
              descriptionParts.push(bandejaDescription);
            }
          } else {
            console.warn(`✗ No se encontraron datos para la orden ${orderId}`);
          }
        });

        // Unir todo sin saltos de línea, solo enter y eliminar espacios al final
        const fullDescription = descriptionParts.join('\n').trim();
        const descriptionField = document.querySelector('textarea[name="invoice[invoice_description]"]');

        if (descriptionField) {
          descriptionField.value = fullDescription;
          // Disparar evento para actualizar el estado del botón
          descriptionField.dispatchEvent(new Event('input', { bubbles: true }));
        } else {
          console.error('✗ No se encontró el textarea');
        }
      })
      .catch(error => {
        console.error('=== ERROR en fetch ===');
        console.error('Error completo:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        console.error('orderIds que se intentaron procesar:', orderIds);
        // Si falla el fetch, no actualizar la descripción (el usuario puede intentar de nuevo)
      });
  }

  updateDescriptionFromAmounts() {
    // Este método se llama cuando cambia el monto de una orden
    // Simplemente reconstruir la descripción (buildOrderDescription ya lee los montos de los inputs)
    const orderSelect = this.orderSelectTarget;
    if (!orderSelect) return;

    // Obtener todas las órdenes seleccionadas
    let selectedOrderIds = [];
    if (orderSelect.slimSelect) {
      const selectedValues = orderSelect.slimSelect.getSelected();
      selectedOrderIds = selectedValues.filter(id => id && id !== '');
    } else {
      const selectedOptions = Array.from(orderSelect.selectedOptions || []);
      selectedOrderIds = selectedOptions.map(opt => opt.value).filter(id => id && id !== '');
    }

    if (selectedOrderIds.length === 0) return;

    // Reconstruir la descripción (el método buildOrderDescription ya lee los montos de los inputs)
    this.buildOrderDescription(selectedOrderIds);
  }

  closeModal() {
    const modal = document.getElementById('createInvoiceModal');
    if (modal && typeof $ !== 'undefined' && $.fn.modal) {
      $(modal).modal('hide');
    }

    // Quitar el foco del modal antes de cerrarlo
    const activeElement = document.activeElement;
    if (activeElement && modal.contains(activeElement)) {
      activeElement.blur();
    }
    if (activeElement === modal) {
      modal.blur();
    }

    // Mover el foco al body
    document.body.focus();

    // Cerrar el modal con Bootstrap
    if (typeof $ !== 'undefined' && $.fn.modal) {
      $(modal).modal('hide');
    }
  }
}
